import { createSelector } from '@ngrx/store';
import get from 'lodash/get';

import { AutoPaySelectors } from '@amfam/billing/auto-pay/data-access';
import { AutoPayUtilSelectors } from '@amfam/billing/auto-pay/util';
import { BillAccountsSelectors as fromBillAccounts } from '@amfam/billing/billaccount/data-access';
import { paymentHistoryQuery } from '@amfam/billing/payment-history/data-access';
import { PaymentMethodSelectors } from '@amfam/billing/payment-method/data-access';
import { PaymentAccountSelectors } from '@amfam/billing/paymentaccount/data-access';
import { ScheduledPaymentSelectors } from '@amfam/billing/schedulepayment/data-access';
import { PolicySelectors as fromPolicies } from '@amfam/policy/data-access';

export const selectBillAccountsListEligible = createSelector(
  fromBillAccounts.selectModifiableBillAccounts,
  AutoPaySelectors.getAutomaticPayments,
  AutoPaySelectors.getAutoPayNotificationEntities,
  (billaccounts, automaticPayments, autopayNotificationEntities) =>
    billaccounts.filter(
      billaccount =>
        !automaticPayments.find(
          automaticPayment =>
            automaticPayment?.billAccountNumber === billaccount.billAccountNumber &&
            !autopayNotificationEntities[automaticPayment.billAccountNumber].hasError
        )
    )
);

// filter eligible billaccounts and check if the property policies has TPI.
export const selectBillAccountsListEligibleForAutopay = createSelector(
  fromPolicies.selectPropertyPoliciesWithTPI,
  selectBillAccountsListEligible,
  (policies, billaccounts) =>
    billaccounts.filter(
      billaccount =>
        !policies.find(
          homePolicy => homePolicy.billingAccountNumber === billaccount.billAccountNumber
        )
    )
);

export const selectCombinedAccountState = createSelector(
  fromBillAccounts.selectUserRegisteredBillAccounts,
  ScheduledPaymentSelectors.selectScheduledPayments,
  AutoPaySelectors.getAutomaticPayments,
  PaymentMethodSelectors.selectPaymentMethods,
  fromBillAccounts.selectBillAccountsAnyLoading,
  ScheduledPaymentSelectors.selectScheduledPaymentsAnyLoading,
  AutoPaySelectors.getAnyLoading,
  PaymentAccountSelectors.selectPaymentAccountsAnyLoading,
  PaymentMethodSelectors.selectLoading,
  ScheduledPaymentSelectors.selectScheduledPaymentsError,
  (
    userRegisteredBillAccounts,
    scheduledPayments,
    automaticPayments,
    paymentMethods,
    billAccountsAnyLoading,
    scheduledPaymentsAnyLoading,
    automaticPaymentsLoading,
    paymentAccountsAnyLoading,
    paymentMethodsLoading,
    scheduledPaymentError
  ) => ({
    userRegisteredBillAccounts,
    scheduledPayments,
    automaticPayments,
    paymentAccounts: paymentMethods,
    loading:
      billAccountsAnyLoading ||
      scheduledPaymentsAnyLoading ||
      paymentAccountsAnyLoading ||
      automaticPaymentsLoading ||
      paymentMethodsLoading,
    error: scheduledPaymentError
  })
);

export const selectCombinedAutoPayState = (billAccountNumber: string) =>
  createSelector(
    AutoPaySelectors.getAutomaticPayments,
    AutoPaySelectors.getAnyLoading,
    ScheduledPaymentSelectors.selectScheduledPayments,
    ScheduledPaymentSelectors.selectScheduledPaymentsLoading,
    selectBillAccountsListEligibleForAutopay,
    AutoPayUtilSelectors.selectBillAccountsEligibleForAutoPay,
    paymentHistoryQuery.selectPayments,
    fromPolicies.selectPropertyPoliciesWithTPI,
    state => fromBillAccounts.selectMatchedBillAccountNotification(state, billAccountNumber),
    (
      automaticPayments,
      automaticPaymentsLoading,
      scheduledPayments,
      scheduledPaymentsLoading,
      oldBillAccountsEligibleForAutoPay,
      billAccountsEligibleForAutoPay,
      pastPayments,
      policiesWithTPI,
      matchedBillAccountNotification
    ) => ({
      automaticPayments,
      scheduledPayments: [...scheduledPayments],
      eligibleBillAccts: billAccountsEligibleForAutoPay,
      loading: automaticPaymentsLoading || scheduledPaymentsLoading,
      matchedBillAccountNotification,
      policiesWithTPI
    })
  );

export const selectFilteredBillAccountNotification = (updateCorrelationId: string) =>
  createSelector(fromBillAccounts.selectAllBillAccountsNotifications, billAccounts => {
    if (!billAccounts || billAccounts.length === 0) {
      return null;
    }

    const filtered = billAccounts.filter(
      ba => get(ba, 'updatePreferenceCorrelationId', ba.updateCorrelationId) === updateCorrelationId
    );

    return filtered.length === 1 ? filtered[0] : null;
  });
